import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  MapPin, ArrowLeft, Scan, List, BarChart3, Loader,
  CheckCircle, AlertCircle, X, Warehouse
} from 'lucide-react';
import { BackButton } from '../../../common/BackButton';
import { useAuth } from '../../../../contexts/AuthContext';
import { useTranslation } from '../../../../contexts/I18nProvider';
import { useFieldRegistration } from '../../../../hooks/useFieldRegistration';
import { warehouseService } from '../../../../services/warehouseService';
import { WarehouseOption } from '../../../../types/warehouse';
import { OverviewPanel } from './OverviewPanel';
import { LocationList } from './LocationList';
import { LocationDetail } from './LocationDetail';
import { ScanTextInput } from '../../../common/inputs';

interface FieldRegistrationAppProps {
  onBack: () => void;
}

export const FieldRegistrationApp: React.FC<FieldRegistrationAppProps> = ({ onBack }) => {
  const { user } = useAuth();
  const { t } = useTranslation();

  // Warehouse selection
  const [warehouseOptions, setWarehouseOptions] = useState<WarehouseOption[]>([]);
  const [selectedWarehouse, setSelectedWarehouse] = useState<string>('');
  const [warehousesLoading, setWarehousesLoading] = useState(true);

  // Scanner input
  const [scanValue, setScanValue] = useState('');
  const scanInputRef = useRef<HTMLInputElement>(null);

  // Main hook
  const {
    view,
    setView,
    loading,
    error,
    allBins,
    binTypes,
    binSizes,
    binGroups,
    movementTypes,
    filteredBins,
    selectedLocation,
    summary,
    filter,
    saving,
    successMessage,
    loadWarehouseData,
    selectLocation,
    selectLocationByBinCode,
    clearSelection,
    updateFilter,
    updateBinProperties,
    updateItemCapacity,
    updateItemStdBinCode,
    bulkApplyCapacity,
    refreshLocation,
    clearError,
    clearSuccess,
    rackSummaries,
  } = useFieldRegistration();

  const dataLoaded = useRef(false);

  // ── Load Warehouse Options ─────────────────────────────────────

  useEffect(() => {
    const loadWarehouses = async () => {
      try {
        const options = await warehouseService.getWarehouseOptions();
        setWarehouseOptions(options);
        if (options.length > 0 && !selectedWarehouse) {
          setSelectedWarehouse(options[0].value);
        }
      } catch (err) {
        console.error('[FieldRegistration] Failed to load warehouses:', err);
        setSelectedWarehouse('1');
      } finally {
        setWarehousesLoading(false);
      }
    };

    loadWarehouses();
  }, []);

  // ── Load Data When Warehouse Selected ──────────────────────────

  useEffect(() => {
    if (selectedWarehouse && !dataLoaded.current) {
      loadWarehouseData(selectedWarehouse);
      dataLoaded.current = true;
    }
  }, [selectedWarehouse, loadWarehouseData]);

  const handleWarehouseChange = (warehouseID: string) => {
    setSelectedWarehouse(warehouseID);
    dataLoaded.current = false;
  };

  // Reset dataLoaded flag when warehouse changes
  useEffect(() => {
    if (selectedWarehouse) {
      dataLoaded.current = false;
      loadWarehouseData(selectedWarehouse);
      dataLoaded.current = true;
    }
  }, [selectedWarehouse]);

  // ── Scanner Handling ───────────────────────────────────────────

  const handleScanSubmit = useCallback(async (value: string) => {
    const trimmed = value.trim();
    if (!trimmed) return;

    // Try to find as location first (bin code)
    await selectLocationByBinCode(trimmed);
    setScanValue('');
  }, [selectLocationByBinCode]);

  const handleScanKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleScanSubmit(scanValue);
    }
  };

  // ── Navigation ─────────────────────────────────────────────────

  const handleBackFromDetail = () => {
    clearSelection();
    setView('location-list');
  };

  const handleBackFromList = () => {
    setView('overview');
  };

  const handleStartRegistration = () => {
    setView('location-list');
  };

  const handleSelectRack = (prefix: string) => {
    updateFilter({ rackPrefix: prefix });
    setView('location-list');
  };

  // ── View Title ─────────────────────────────────────────────────

  const getViewTitle = () => {
    switch (view) {
      case 'overview': return t('warehouse.modules.fieldRegistration.title', {}, 'Lokasjonsvedlikehold');
      case 'location-list': return filter.rackPrefix
        ? t('warehouse.modules.fieldRegistration.app.rackTitle', { '0': filter.rackPrefix }, `Reol ${filter.rackPrefix}`)
        : t('warehouse.modules.fieldRegistration.app.locationsTitle', {}, 'Lokasjoner');
      case 'location-detail': return selectedLocation?.bin.binCode || t('warehouse.general.bin', {}, 'Lokasjon');
      case 'scan': return t('warehouse.modules.fieldRegistration.app.scanTitle', {}, 'Skann lokasjon');
      default: return t('warehouse.modules.fieldRegistration.title', {}, 'Lokasjonsvedlikehold');
    }
  };

  const getBackAction = () => {
    switch (view) {
      case 'overview': return onBack;
      case 'location-list': return handleBackFromList;
      case 'location-detail': return handleBackFromDetail;
      case 'scan': return () => setView('location-list');
      default: return onBack;
    }
  };

  // ── Render ─────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-20">
        <div className="max-w-4xl mx-auto px-3 sm:px-4">
          {/* Top Row: Back + Title + Warehouse Selector */}
          <div className="flex items-center justify-between py-3 min-h-[52px]">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <BackButton onClick={getBackAction()} label="" iconSize={20} />
              <h1 className="text-base sm:text-lg font-bold text-gray-900 truncate">
                {getViewTitle()}
              </h1>
            </div>

            {/* Warehouse selector */}
            {!warehousesLoading && warehouseOptions.length > 1 && (
              <select
                value={selectedWarehouse}
                onChange={(e) => handleWarehouseChange(e.target.value)}
                className="text-xs border border-gray-300 rounded-lg px-2 py-1.5 bg-white min-h-[32px]"
              >
                {warehouseOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            )}
          </div>

          {/* Scanner Bar (always visible except on overview) */}
          {view !== 'overview' && (
            <div className="pb-3">
              <div className="relative">
                <Scan size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#0E7C86]" />
                <ScanTextInput
                  ref={scanInputRef}
                  value={scanValue}
                  onChange={(e) => setScanValue(e.target.value)}
                  onKeyDown={handleScanKeyDown}
                  placeholder={t('warehouse.modules.fieldRegistration.app.scanPlaceholder', {}, 'Skann eller skriv lokasjonskode...')}
                  className="w-full pl-10 pr-4 py-2.5 border-2 border-[#0E7C86] rounded-lg text-sm bg-[#E0F2F4]/30 focus:outline-none focus:bg-white focus:ring-1 focus:ring-[#0E7C86] min-h-[44px] font-mono"
                />
              </div>
            </div>
          )}

          {/* View Tabs (on list views) */}
          {(view === 'location-list' || view === 'overview') && allBins.length > 0 && (
            <div className="flex border-t border-gray-100 -mx-3 sm:-mx-4 px-3 sm:px-4">
              <button
                onClick={() => setView('overview')}
                className={`flex items-center gap-1.5 px-4 py-2.5 text-xs font-medium border-b-2 transition-colors min-h-[40px] ${
                  view === 'overview'
                    ? 'border-[#0E7C86] text-[#0E7C86]'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <BarChart3 size={14} />
                {t('warehouse.modules.fieldRegistration.app.tabOverview', {}, 'Oversikt')}
              </button>
              <button
                onClick={() => setView('location-list')}
                className={`flex items-center gap-1.5 px-4 py-2.5 text-xs font-medium border-b-2 transition-colors min-h-[40px] ${
                  view === 'location-list'
                    ? 'border-[#0E7C86] text-[#0E7C86]'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <List size={14} />
                {t('warehouse.modules.fieldRegistration.app.tabLocations', { '0': allBins.length }, `Lokasjoner (${allBins.length})`)}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="max-w-4xl mx-auto px-3 sm:px-4 pt-3">
          <div className="bg-green-50 border border-green-200 text-green-800 rounded-lg p-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <CheckCircle size={16} className="text-green-500" />
              <span className="text-sm font-medium">{successMessage}</span>
            </div>
            <button onClick={clearSuccess} className="text-green-500 hover:text-green-700">
              <X size={16} />
            </button>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="max-w-4xl mx-auto px-3 sm:px-4 pt-3">
          <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg p-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <AlertCircle size={16} className="text-red-500" />
              <span className="text-sm font-medium">{error}</span>
            </div>
            <button onClick={clearError} className="text-red-500 hover:text-red-700">
              <X size={16} />
            </button>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-3 sm:px-4 py-4 pb-20">
        {/* Loading State */}
        {loading && !selectedLocation ? (
          <div className="flex flex-col items-center justify-center py-12">
            <Loader size={32} className="animate-spin text-[#0E7C86] mb-3" />
            <p className="text-sm text-gray-500">{t('warehouse.modules.fieldRegistration.app.loadingData', {}, 'Laster lagerdata...')}</p>
          </div>
        ) : (
          <>
            {/* Overview */}
            {view === 'overview' && (
              <OverviewPanel
                summary={summary}
                rackSummaries={rackSummaries}
                totalBins={allBins.length}
                onStartRegistration={handleStartRegistration}
                onSelectRack={handleSelectRack}
              />
            )}

            {/* Location List */}
            {view === 'location-list' && (
              <LocationList
                bins={filteredBins}
                binTypes={binTypes}
                binSizes={binSizes}
                filter={filter}
                rackSummaries={rackSummaries}
                onUpdateFilter={updateFilter}
                onSelectLocation={selectLocation}
              />
            )}

            {/* Location Detail */}
            {view === 'location-detail' && selectedLocation && (
              <LocationDetail
                location={selectedLocation}
                saving={saving}
                binGroups={binGroups}
                movementTypes={movementTypes}
                allBins={allBins}
                onUpdateBinProperties={updateBinProperties}
                onUpdateItemCapacity={updateItemCapacity}
                onUpdateItemStdBinCode={updateItemStdBinCode}
                onBulkApplyCapacity={bulkApplyCapacity}
              />
            )}

            {/* Loading overlay for detail */}
            {loading && selectedLocation && (
              <div className="fixed inset-0 bg-black/20 flex items-center justify-center z-30">
                <div className="bg-white rounded-lg p-4 flex items-center gap-3 shadow-lg">
                  <Loader size={20} className="animate-spin text-[#0E7C86]" />
                  <span className="text-sm text-gray-700">{t('warehouse.modules.fieldRegistration.app.updating', {}, 'Oppdaterer...')}</span>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};
